import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    MessageSquare,
    Store,
    User,
    Mail,
    Phone,
    Calendar,
    CheckCircle,
    Clock,
    AlertCircle,
    XCircle
} from 'lucide-react';
import { useGetMarketplaceInquiriesQuery } from '../../store/slices/apiSlice';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { useRespondMarketplaceInquiryMutation } from '../../store/slices/apiSlice';
import toast from 'react-hot-toast';

const MarketplaceInquiryDetails: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const inquiryId = id ? parseInt(id) : null;
    const isValidId = inquiryId && !isNaN(inquiryId) && inquiryId > 0;

    const { data: inquiriesData, isLoading, error, refetch } = useGetMarketplaceInquiriesQuery({
        page: 1,
        limit: 1000
    });

    const [showReplyModal, setShowReplyModal] = useState(false);
    const [replyMessage, setReplyMessage] = useState('');
    const [replyStatus, setReplyStatus] = useState<'new' | 'in_progress' | 'resolved'>('in_progress');
    const [respondInquiry, { isLoading: isResponding }] = useRespondMarketplaceInquiryMutation();

    const inquiries = inquiriesData?.data?.inquiries || [];
    const inquiry = inquiries.find(i => i.inquiry_id === inquiryId);

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'new':
                return <Badge variant="warning" className="flex items-center gap-1"><AlertCircle className="w-3 h-3" />New</Badge>;
            case 'in_progress':
                return <Badge variant="default" className="flex items-center gap-1"><Clock className="w-3 h-3" />In Progress</Badge>;
            case 'resolved':
                return <Badge variant="success" className="flex items-center gap-1"><CheckCircle className="w-3 h-3" />Resolved</Badge>;
            default:
                return <Badge variant="default">{status}</Badge>;
        }
    };

    const formatDate = (dateString: string | Date | null | undefined) => formatDateTimeUtil(dateString);;

    const handleRespond = () => {
        if (inquiry) {
            setReplyMessage(inquiry.admin_response || '');
            setReplyStatus(inquiry.status);
            setShowReplyModal(true);
        }
    };

    const handleSendReply = async () => {
        if (!inquiry) return;

        try {
            await respondInquiry({
                inquiry_id: inquiry.inquiry_id,
                status: replyStatus,
                admin_response: replyMessage.trim() || undefined
            }).unwrap();
            toast.success('Inquiry response saved successfully');
            setShowReplyModal(false);
            setReplyMessage('');
            refetch();
        } catch (error: any) {
            toast.error(error?.data?.message || 'Failed to respond to inquiry');
        }
    };

    if (!isValidId) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <p className="text-red-800">Invalid inquiry ID</p>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    if (error || !inquiry) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <p className="text-red-800">Error loading inquiry. Please try again.</p>
                    <button
                        onClick={() => navigate('/admin/marketplace-inquiries')}
                        className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/admin/marketplace-inquiries')}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Inquiry Details</h1>
                                <p className="text-gray-600">View and manage inquiry information</p>
                            </div>
                        </div>
                        <Button
                            onClick={handleRespond}
                            variant="outline"
                            className="bg-white hover:bg-gray-50"
                        >
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Respond
                        </Button>
                    </div>
                </div>
            </div>

            <div className="px-6 py-6">
                <div className="max-w-4xl mx-auto space-y-6">
                    {/* Inquiry Information */}
                    <div className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                <MessageSquare className="w-5 h-5 mr-2" />
                                Inquiry Information
                            </h2>
                        </div>
                        <div className="p-6 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Status</label>
                                    <div className="mt-1">{getStatusBadge(inquiry.status)}</div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Created At</label>
                                    <div className="mt-1 flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-gray-400" />
                                        <p className="text-sm text-gray-900">{formatDate(inquiry.created_at)}</p>
                                    </div>
                                </div>
                            </div>

                            {inquiry.listing && (
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Listing</label>
                                    <div className="mt-1 flex items-center gap-2">
                                        <Store className="w-4 h-4 text-gray-400" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">{inquiry.listing.title}</p>
                                            <p className="text-xs text-gray-500 capitalize">{inquiry.listing.listing_type}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="text-sm font-medium text-gray-700">User Information</label>
                                <div className="mt-2 space-y-2">
                                    <div className="flex items-center gap-2">
                                        <User className="w-4 h-4 text-gray-400" />
                                        <p className="text-sm text-gray-900">{inquiry.user?.name || 'Guest'}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Mail className="w-4 h-4 text-gray-400" />
                                        <p className="text-sm text-gray-900">{inquiry.contact_email}</p>
                                    </div>
                                    {inquiry.contact_phone && (
                                        <div className="flex items-center gap-2">
                                            <Phone className="w-4 h-4 text-gray-400" />
                                            <p className="text-sm text-gray-900">{inquiry.contact_phone}</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="text-sm font-medium text-gray-700">Message</label>
                                <div className="mt-2 p-4 bg-gray-50 rounded-lg">
                                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{inquiry.message}</p>
                                </div>
                            </div>

                            {inquiry.admin_response && (
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Admin Response</label>
                                    <div className="mt-2 p-4 bg-white border border-gray-200 rounded-lg">
                                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{inquiry.admin_response}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Reply Modal */}
            <Modal
                isOpen={showReplyModal}
                onClose={() => {
                    setShowReplyModal(false);
                    setReplyMessage('');
                }}
                title="Respond to Inquiry"
            >
                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                        <select
                            value={replyStatus}
                            onChange={(e) => setReplyStatus(e.target.value as any)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="new">New</option>
                            <option value="in_progress">In Progress</option>
                            <option value="resolved">Resolved</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Admin Response</label>
                        <textarea
                            value={replyMessage}
                            onChange={(e) => setReplyMessage(e.target.value)}
                            rows={6}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter your response..."
                        />
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                        <Button
                            onClick={() => {
                                setShowReplyModal(false);
                                setReplyMessage('');
                            }}
                            variant="outline"
                            disabled={isResponding}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSendReply}
                            variant="outline"
                            className="bg-white hover:bg-gray-50"
                            disabled={isResponding}
                            isLoading={isResponding}
                        >
                            Save Response
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default MarketplaceInquiryDetails;

